import { useParams } from "react-router";
import { IoIosCamera } from "react-icons/io";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { FaX, FaCheck } from "react-icons/fa6";
import { RxCaretRight, RxCaretLeft } from "react-icons/rx";
import { useGetAsset } from "@/hooks/useAsset";
import useGetOverwolfGameInfo from "@/hooks/useGetOverwolfGameInfo";
import CircularProgressIndicator from "@/components/CircularProgress";
import { useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { useGetChallenge } from "@/hooks/challenges/useGetChallenge";
import { ChallengeInfoResponse, ChallengeSegment, QuestsApi, QuestConfigMilestone, PhotoType, WebsocketEvent, MilestoneSubtype } from "@devour/client";
import { useSubmitChallengeResponse } from "@/hooks/useSubmitChallengeResponse";
import getConfig from "@/utils/getConfig";
import { logClickUrlEvent } from "@/utils/logClickUrlEvent";
import { getIconForUrl, detectPlatformFromUrl, generateDeepLink, getPlatformConfig } from "@/utils/socialDeepLinks";
import {Modal} from "@/widgets/Modal";
import {useDispatch, useSelector} from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { useValidateQuestPhoto } from "@/hooks/useValidateQuestPhoto";
import Skeleton from "react-loading-skeleton";
import BrandLandingFooter from "@/components/brands/BrandLandingFooter";
import { ChallengeAcceptModal } from "@/pages/challenge/ChallengeAcceptModal";
import { ChallengeNavbar } from "@/pages/challenge/ChallengeNavbar";
import { ChallengeHero } from "@/pages/challenge/ChallengeHero";
import { BrandChallengeCountdown } from "@/pages/challenge/BrandChallengeCountdown";
import { BrandChallengesPrizeNotifier } from "@/pages/challenge/BrandChallengesPrizeNotifier";
import { ChallengeVideo } from "@/pages/challenge/ChallengeVideo";
import ChallengePromotionsSection from "@/pages/challenge/ChallengePromotionsSection";
import { ChallengeFAQ } from "@/pages/challenge/ChallengeFAQ";
import { ChallengeHowToPlay } from "@/pages/challenge/ChallengeHowToPlay";
import { ChallengeLoginModal } from "@/components/modals/ChallengeLoginModal";
import ScreenshotLightbox from "@/components/games/pages/sections/ScreenshotLightbox";
import {useGetPartnerService} from "@/hooks/useGetPartnerService";
import {newReferralCode} from "@/redux/affiliateReferrals/affiliateActions";
import useWebsocket from "@/hooks/useWebsocket";
import SEO from "@/components/SEO";
import { InviteModal, InviteModalType } from "@/components/InviteModal";

// Generic photo type description - milestone data should override these defaults
const getPhotoTypeDescription = (_photoType: PhotoType) => {
    return {
        title: "PHOTO CHALLENGE",
        subtitle: "",
        game: "",
        map: "",
        vehicle: "",
        exampleImageUrl: "", // No default image - will use milestone's photoExampleAsset
        requirements: [],
    };
};

// SEO config type - matches backend PageSeo model
interface ChallengeSeoConfig {
    title?: string;
    description?: string;
    socialShareImageId?: string;
    faviconId?: string;
    canonicalUrl?: string;
}

// Theme colors type - matches backend ChallengeThemeColors model
interface ChallengeThemeColors {
    primary?: string;
    primaryDark?: string;
    secondary?: string;
    secondaryDark?: string;
    accent?: string;
    background?: string;
    cardBackground?: string;
    textPrimary?: string;
    textSecondary?: string;
    success?: string;
    error?: string;
}

// Extended challenge type for fields not yet in generated client
interface ExtendedChallengeInfo extends ChallengeInfoResponse {
    seo?: ChallengeSeoConfig;
    themeColors?: ChallengeThemeColors;
}

const getCurrentSegmentEntries = (challenge: ChallengeInfoResponse) => {
    // Find the current active segment index (the displayTab segment that's currently active)
    const segments = challenge?.segments ?? [];
    const now = Date.now();

    // Find the current weekly segment (displayTab: true and active)
    const currentSegment = segments.find((segment) => segment.displayTab === true && now >= segment.startAt && now <= segment.endAt);
    const currentSegmentIndex = segments.findIndex((segment) => segment.displayTab === true && now >= segment.startAt && now <= segment.endAt);

    // Find the Full Campaign segment (displayTab: false, typically index 0)
    const fullCampaignSegment = segments.find((segment) => segment.displayTab === false && now >= segment.startAt && now <= segment.endAt);

    if (currentSegmentIndex === -1 || !currentSegment) return 0;

    const progressions = challenge?.progressions ?? [];

    // Check if ANY progression has entriesBySegment populated
    const hasEntriesBySegment = progressions.some(prog => {
        const ebs = (prog as any).entriesBySegment;
        return ebs && Object.keys(ebs).length > 0;
    });

    if (hasEntriesBySegment) {
        // Use entriesBySegment (tracks entries by active segment at time of completion)
        return progressions.reduce((sum, prog) => {
            const entriesBySegment = (prog as any).entriesBySegment as Record<number, number> | undefined;
            return sum + (entriesBySegment?.[currentSegmentIndex] || 0);
        }, 0);
    }

    /*
     * Fallback: Sum pointsTotal from BOTH weekly segment AND Full Campaign progressions
     * Full Campaign activities should count toward weekly entries
     */
    let total = 0;
    const segmentProg = progressions.find(p => p.questConfigId === currentSegment.mainQuestId);
    if (segmentProg) total += segmentProg.pointsTotal || 0;

    if (fullCampaignSegment) {
        const fullCampaignProg = progressions.find(p => p.questConfigId === fullCampaignSegment.mainQuestId);
        if (fullCampaignProg) total += fullCampaignProg.pointsTotal || 0;
    }

    return total;
};

/**
 * Get total entries by summing entriesBySegment across ALL segments from ALL progressions.
 * Falls back to pointsTotal when entriesBySegment is not populated.
 */
const getTotalSegmentEntries = (challenge: ChallengeInfoResponse) => {
    const progressions = challenge?.progressions ?? [];

    // Check if ANY progression has entriesBySegment populated
    const hasEntriesBySegment = progressions.some(prog => {
        const ebs = (prog as any).entriesBySegment;
        return ebs && Object.keys(ebs).length > 0;
    });

    if (hasEntriesBySegment) {
        return progressions.reduce((totalSum, prog) => {
            const entriesBySegment = (prog as any).entriesBySegment as Record<number, number> | undefined;
            if (!entriesBySegment) return totalSum;
            const progSum = Object.values(entriesBySegment).reduce((sum, entries) => sum + (entries || 0), 0);
            return totalSum + progSum;
        }, 0);
    }

    // Fallback: sum pointsTotal from ALL progressions
    return progressions.reduce((sum, prog) => sum + (prog.pointsTotal || 0), 0);
};

/**
 * Get Grand Prize entries by summing entriesBySegment across ALL segments from ALL progressions.
 * entriesBySegment is the canonical tracking mechanism - it correctly tracks entries
 * from both weekly segment quests AND Full Campaign quest activities.
 */
const getGrandPrizeEntries = (challenge: ChallengeInfoResponse) => {
    // Cast to include grandPrize field (not yet in generated types)
    const extendedChallenge = challenge as ChallengeInfoResponse & {
        grandPrize?: { enabled?: boolean };
    };

    // If grandPrize config exists and is disabled, return 0
    if (extendedChallenge.grandPrize && !extendedChallenge.grandPrize.enabled) {
        return 0;
    }

    // Use getTotalSegmentEntries for the actual calculation
    return getTotalSegmentEntries(challenge);
};

// Check if Grand Prize is enabled for this challenge
const isGrandPrizeEnabled = (challenge: ChallengeInfoResponse): boolean => {
    const extendedChallenge = challenge as ChallengeInfoResponse & {
        grandPrize?: { enabled?: boolean };
    };
    // If no grandPrize config, default to true for backward compatibility
    return extendedChallenge.grandPrize?.enabled ?? true;
};

// ChallengeVideo component moved to separate file: ChallengeVideo.tsx

const PhotoModal = (props: {
    photoType: PhotoType,
    isOpen: boolean,
    onClose: () => void,
    onSubmit: () => void,
    entriesEarned?: number,
    milestone?: QuestConfigMilestone,
    refetch?: () => void,
}) => {
    const defaultDescription = getPhotoTypeDescription(props.photoType);
    const milestoneResponse = props.milestone as any;

    /* Use milestone data if available, fallback to hardcoded defaults */
    const description = {
        ...defaultDescription,
        title: milestoneResponse?.name || defaultDescription.title,
        exampleImageUrl: milestoneResponse?.photoExampleAsset?.url || defaultDescription.exampleImageUrl,
        instructions: milestoneResponse?.photoInstructions || undefined,
    };

    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const [_selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(description.exampleImageUrl);
    const [uploadStatus, setUploadStatus] = useState<"idle" | "success" | "error">("idle");
    const [isClaiming, setIsClaiming] = useState(false);
    const [isClaimed, setIsClaimed] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Reset state when modal opens
    useEffect(() => {
        if (props.isOpen) {
            setSelectedFile(null);
            // Use milestone example image if available, otherwise show no preview
            setPreviewUrl(description.exampleImageUrl || null);
            setUploadStatus("idle");
            setIsClaiming(false);
            setIsClaimed(false);
        }
    }, [props.isOpen]);

    const validatePhotoMutation = useValidateQuestPhoto(fullToken);


    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setUploadStatus("idle");

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result as string);
            };
            reader.readAsDataURL(file);

            // Submit the file
            await submitPhoto(file);
        }
    };

    const submitPhoto = async (file: File) => {
        const result = await validatePhotoMutation.mutateAsync({
            photo: file,
            photoType: props.photoType,
        });
        if (result.success) {
            setUploadStatus("success");
        } else {
            setUploadStatus("error");
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    // Handle claim action for photo milestone
    const handleClaim = async () => {
        if (!fullToken || !props.milestone || isClaiming || isClaimed) return;
        setIsClaiming(true);
        try {
            await new QuestsApi(getConfig(fullToken)).createQuestConfigsIdClaimReward({
                id: props.milestone.id,
            });
            setIsClaimed(true);
            props.refetch?.();
        } catch (err) {
            console.error("Failed to claim photo reward:", err);
        } finally {
            setIsClaiming(false);
        }
    };

    return (
        <Modal
            isOpen={props.isOpen}
            toggle={props.onClose}
            size="xs"
            contentClassName="challenge-page_photo-modal-container"
        >
            <div className="challenge-page_photo-modal">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                />
                <div className="challenge-page_photo-modal_close-button-row" onClick={props.onClose}>
                    <FaX size={16} color="#87878D"/>
                </div>

                <div className="challenge-page_photo-modal_header">
                    <div className="challenge-page_photo-modal_title">
                        {description.title}
                    </div>
                    <div className="challenge-page_photo-modal_subtitle">
                        {description.subtitle}
                    </div>
                </div>

                {uploadStatus !== "success" && !description.instructions &&
                    <div className="challenge-page_photo-modal_instructions">
                        Capture and Upload an in-game screen shot based on the instructions below.
                    </div>
                }

                <div className={`challenge-page_photo-modal_image ${uploadStatus}`}>
                    <img src={previewUrl} alt="Example" className="challenge-page_photo-modal_image_preview" />
                    {validatePhotoMutation.isPending &&
                        <div className="challenge-page_photo-modal_image_status loading">
                            <div className="challenge-page_photo-modal_spinner" />
                        </div>
                    }
                    {uploadStatus === "success" &&
                        <div className="challenge-page_photo-modal_image_status success">
                            <FaCheck size={32} color="#10B981" />
                        </div>
                    }
                    {uploadStatus === "error" &&
                        <div className="challenge-page_photo-modal_image_status error">
                            X
                        </div>
                    }
                </div>

                {uploadStatus === "success" &&
                    <div className="challenge-page_photo-modal_success">
                        <div className="challenge-page_photo-modal_success_title">
                            {isClaimed ? "REWARD CLAIMED!" : "OP COMPLETE!"}
                        </div>
                        <div className="challenge-page_photo-modal_success_text">
                            {isClaimed
                                ? "You have successfully earned both weekly and grand prize entries."
                                : "Your photo has been validated. Claim your reward below!"
                            }
                        </div>
                    </div>
                }

                {uploadStatus === "error" &&
                    <div className="challenge-page_photo-modal_error">
                        <span className="challenge-page_photo-modal_error_oops">Oops!</span>
                        {" "}Your image is missing one of its requirements. Please Try Again!
                    </div>
                }

                {uploadStatus !== "success" && description.instructions &&
                    <div
                        className="challenge-page_photo-modal_details challenge-page_photo-modal_details--rich-text"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description.instructions) }}
                    />
                }

                {uploadStatus !== "success" && !description.instructions &&
                    <div className="challenge-page_photo-modal_details">
                        <div className="challenge-page_photo-modal_detail">
                            <span className="challenge-page_photo-modal_detail_label">GAME:</span> {description.game}
                        </div>
                        <div className="challenge-page_photo-modal_detail">
                            <span className="challenge-page_photo-modal_detail_label">MAP:</span> {description.map}
                        </div>
                        <div className="challenge-page_photo-modal_detail">
                            <span className="challenge-page_photo-modal_detail_label">VEHICLE:</span> {description.vehicle}
                        </div>
                        {description.requirements?.map((req, index) =>
                            <div key={index} className="challenge-page_photo-modal_detail">
                                <span className="challenge-page_photo-modal_detail_label">REQUIREMENT {index + 1}:</span> {req}
                            </div>)}
                    </div>
                }

                <div className="challenge-page_photo-modal_button-row">
                    {uploadStatus !== "success" &&
                        <button
                            onClick={handleUploadClick}
                            className="challenge-page_photo-modal_upload-button"
                            disabled={validatePhotoMutation.isPending}
                        >
                            {validatePhotoMutation.isPending ? "Validating..." : "Upload Photo"}
                        </button>
                    }
                    {uploadStatus === "success" && !isClaimed && props.milestone &&
                        <button
                            onClick={handleClaim}
                            className="challenge-page_photo-modal_claim-button"
                            disabled={isClaiming}
                        >
                            {isClaiming ? "Claiming..." : `Claim ${props.entriesEarned || ""} Entries`}
                        </button>
                    }
                    <button onClick={props.onClose} className="challenge-page_photo-modal_close-button">
                        {uploadStatus === "success" && isClaimed ? "Done" : "Close"}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

// HowToStep and HowToArrow components moved to ChallengeHowToPlay.tsx

interface TimeLeftProps {
    challenge: ChallengeInfoResponse;
}

interface SegmentTimeLeft {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
}

const timeLeftToString = (timeLeft: SegmentTimeLeft) => {
    const { days, hours, minutes, seconds } = timeLeft;
    let result = "";
    if (days > 0) {
        result += `${days}d `;
    }
    if (hours > 0) {
        result += `${hours}h `;
    }
    result += `${minutes.toString().padStart(2, "0")}m ${seconds.toString().padStart(2, "0")}s`;
    return result.trim();
};

const _getCurrentAndLastSegment = (challenge: ChallengeInfoResponse) => {
    const segments = challenge?.segments ?? [];
    // find the segment with the latest endAt
    const latestSegment = segments.reduce((latest, segment) => {
        const segmentEnd = new Date(segment.endAt);
        return segmentEnd > latest.endAt ? { ...segment, endAt: segmentEnd } : latest;
    }, { endAt: new Date(0) });

    // Get all currently active segments (for milestones - includes Full Campaign)
    const activeSegments = segments.filter(segment => {
        const now = new Date();
        const startDate = new Date(segment.startAt);
        const endDate = new Date(segment.endAt);
        return now >= startDate && now <= endDate;
    });

    // For the title, use the current segment that has displayTab: true (weekly segments)
    const displayCurrentSegment = activeSegments.find(segment => segment.displayTab);

    // For milestones, we still want all active segments (can be used later if needed)
    const currentSegment = activeSegments[0];

    return {
        currentSegment,
        displayCurrentSegment,
        latestSegment: latestSegment as any as ChallengeSegment,
    } as {
        currentSegment: ChallengeSegment | undefined;
        displayCurrentSegment: ChallengeSegment | undefined;
        latestSegment: ChallengeSegment | undefined;
    };
};

export const TimeLeft = ({ challenge }: TimeLeftProps) => {
    // Tick state to force re-render every second
    const [tick, setTick] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setTick(t => t + 1);
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const segments = challenge?.segments ?? [];
    const now = Date.now();

    // Find first segment (earliest startAt) with displayTab for countdown
    const sortedDisplaySegments = segments
        .filter(s => s.displayTab)
        .sort((a, b) => a.startAt - b.startAt);
    const firstSegment = sortedDisplaySegments[0];

    // Find the segment with the latest endAt (for grand prize)
    const latestSegment = segments.reduce((latest, segment) => {
        const segmentEnd = new Date(segment.endAt);
        return segmentEnd > latest.endAt ? { ...segment, endAt: segmentEnd } : latest;
    }, { endAt: new Date(0) });

    // Get all currently active segments
    const activeSegments = segments.filter(segment => {
        const startDate = new Date(segment.startAt);
        const endDate = new Date(segment.endAt);
        return now >= startDate.getTime() && now <= endDate.getTime();
    });

    // For the timer, use the current weekly segment (displayTab: true), not Full Campaign
    const currentSegment = activeSegments.find(segment => segment.displayTab) || activeSegments[0];

    /*
     * Determine challenge state
     * Challenge ended = current time is past the latest segment's end time
     */
    const challengeNotStarted = firstSegment && now < firstSegment.startAt;
    const latestEndTime = latestSegment ? new Date(latestSegment.endAt).getTime() : 0;
    const challengeEnded = challenge?.hasEnded === true || now > latestEndTime;

    // Time until first segment starts (for pre-challenge countdown)
    const timeUntilStart: SegmentTimeLeft = useMemo(() => {
        if (!firstSegment || !challengeNotStarted) {
            return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }
        const totalMs = Math.max(0, firstSegment.startAt - now);

        const seconds = Math.floor(totalMs / 1000 % 60);
        const minutes = Math.floor(totalMs / (1000 * 60) % 60);
        const hours = Math.floor(totalMs / (1000 * 60 * 60) % 24);
        const days = Math.floor(totalMs / (1000 * 60 * 60 * 24));

        return { days, hours, minutes, seconds };
    }, [firstSegment, challengeNotStarted, tick]);

    const grandPrizeTimeLeft: SegmentTimeLeft = useMemo(() => {
        if (!latestSegment) {
            return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }
        const totalMs = new Date(latestSegment.endAt).getTime() - now;

        const clamped = Math.max(0, totalMs);

        const seconds = Math.floor(clamped / 1000 % 60);
        const minutes = Math.floor(clamped / (1000 * 60) % 60);
        const hours = Math.floor(clamped / (1000 * 60 * 60) % 24);
        const days = Math.floor(clamped / (1000 * 60 * 60 * 24));

        return { days, hours, minutes, seconds };
    }, [latestSegment, tick]);

    const currentSegmentTimeLeft: SegmentTimeLeft = useMemo(() => {
        if (!currentSegment) {
            return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }
        const totalMs = new Date(currentSegment.endAt).getTime() - now;

        const clamped = Math.max(0, totalMs);

        const seconds = Math.floor(clamped / 1000 % 60);
        const minutes = Math.floor(clamped / (1000 * 60) % 60);
        const hours = Math.floor(clamped / (1000 * 60 * 60) % 24);
        const days = Math.floor(clamped / (1000 * 60 * 60 * 24));

        return { days, hours, minutes, seconds };
    }, [currentSegment, tick]);

    // Pre-challenge state: show countdown to start
    if (challengeNotStarted) {
        return (
            <div className="challenge-page_blue-border">
                <div className="challenge-page_time-left" style={{width: "100%"}}>
                    <div style={{width: "50%", marginLeft: "25px", marginRight: "25px"}}>
                        <div className="challenge-page_time-left_segment-title">
                            {firstSegment?.title} Starts In
                        </div>
                        <div className="challenge-page_time-left_segment-timer">
                            {timeLeftToString(timeUntilStart)}
                        </div>
                    </div>
                    <div style={{width: "50%", marginLeft: "25px", marginRight: "25px"}}>
                        <div className="challenge-page_time-left_segment-title">
                            Grand Prize Giveaway Ends
                        </div>
                        <div className="challenge-page_time-left_segment-timer">
                            {timeLeftToString(grandPrizeTimeLeft)}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Challenge ended state
    if (challengeEnded) {
        return (
            <div className="challenge-page_blue-border">
                <div className="challenge-page_time-left" style={{width: "100%"}}>
                    <div style={{width: "100%", marginLeft: "25px", marginRight: "25px", textAlign: "center"}}>
                        <div className="challenge-page_time-left_segment-title">
                            Challenge Has Ended
                        </div>
                        <div className="challenge-page_time-left_segment-timer">
                            Winners will be announced soon!
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Active challenge state (default)
    return (
        <div className="challenge-page_blue-border">
            <div className="challenge-page_time-left" style={{width: "100%"}}>
                <div style={{width: "50%", marginLeft: "25px", marginRight: "25px"}}>
                    <div className="challenge-page_time-left_segment-title">
                        {currentSegment?.title} Giveaway Ends
                    </div>
                    <div className="challenge-page_time-left_segment-timer">
                        {timeLeftToString(currentSegmentTimeLeft)}
                    </div>
                </div>
                <div style={{width: "50%", marginLeft: "25px", marginRight: "25px"}}>
                    <div className="challenge-page_time-left_segment-title">
                        Grand Prize Giveaway Ends
                    </div>
                    <div className="challenge-page_time-left_segment-timer">
                        {timeLeftToString(grandPrizeTimeLeft)}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Extended challenge type for entriesColumnStyles (until client types are regenerated)
interface EntriesColumnStyles {
    backgroundColor?: string;
    textColor?: string;
    accentColor?: string;
}

// Card tracker color config per milestone subtype
type MilestoneSubtypeValue = "GAME" | "PHOTO" | "SOCIAL" | "WEBHOOK" | "REFERRAL";

interface CardTrackerColorConfig {
    subtype: MilestoneSubtypeValue;
    backgroundColor?: string;
    borderColor?: string;
    textColor?: string;
    accentColor?: string;
    iconColor?: string;
}

interface QuestsSectionConfig extends EntriesColumnStyles {
    cardTrackers?: CardTrackerColorConfig[];
}

interface ChallengeWithEntriesStyles extends ChallengeInfoResponse {
    entriesColumnStyles?: EntriesColumnStyles;
    questsStyles?: QuestsSectionConfig;
}

interface MyWeeklyEntriesProps {
    challenge: ChallengeInfoResponse;
    segmentTitle?: string;
}

export const MyWeeklyEntries = ({ challenge, segmentTitle }: MyWeeklyEntriesProps) => {
    // Cast to extended type to access entriesColumnStyles
    const extendedChallenge = challenge as ChallengeWithEntriesStyles;
    const entriesStyles = extendedChallenge?.entriesColumnStyles;

    const currentSegmentEntries = getCurrentSegmentEntries(challenge);
    const grandPrizeEntries = getGrandPrizeEntries(challenge);
    const showGrandPrize = isGrandPrizeEnabled(challenge);

    // Use segment title for entry labels, fallback to "Current" if not provided
    const entryLabel = segmentTitle ? `My ${segmentTitle} Entries` : "My Entries";

    /*
     * Priority: entriesColumnStyles > themeColors.primary > brandColor
     * Uses CSS variable as ultimate fallback (which is already set with proper priority)
     */
    const backgroundColor = entriesStyles?.backgroundColor || "var(--challenge-primary)";
    const textColor = entriesStyles?.textColor || "#FFFFFF";

    const containerStyle: React.CSSProperties = {
        width: "100%",
        backgroundColor,
        color: textColor,
    };

    return (
        <div className="challenge-page_my-weekly-entries" style={containerStyle}>
            <div style={{width: showGrandPrize ? "50%" : "100%", marginLeft: "25px", marginRight: "25px"}}>
                <div className="challenge-page_my-weekly-entries_segment-title">
                    {entryLabel}
                </div>
                <div className="challenge-page_my-weekly-entries_segment-timer">
                    {currentSegmentEntries}
                </div>
            </div>
            {showGrandPrize &&
                <div style={{width: "50%", marginLeft: "25px", marginRight: "25px"}}>
                    <div className="challenge-page_my-weekly-entries_segment-title">
                            My Grand Prize Entries
                    </div>
                    <div className="challenge-page_my-weekly-entries_segment-timer">
                        {grandPrizeEntries}
                    </div>
                </div>
            }
        </div>
    );
};

interface GrandPrizeCarouselProps {
    images?: string[];
}

export const GrandPrizeCarousel = ({ images: propImages }: GrandPrizeCarouselProps) => {
    // No fallback - if no images provided, return null (parent should hide section)
    const images = propImages?.length ? propImages : [];

    const [imageIndex, setImageIndex] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);

    // Don't render if no images
    if (images.length === 0) {
        return null;
    }

    const handlePrevClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setImageIndex((prevIndex) => prevIndex === 0 ? images.length - 1 : prevIndex - 1);
    };

    const handleNextClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setImageIndex((prevIndex) => prevIndex === images.length - 1 ? 0 : prevIndex + 1);
    };

    return (
        <>
            <div
                className="challenge-page_grand-prize_carousel"
                onClick={() => setLightboxOpen(true)}
                style={{ cursor: "pointer" }}
            >
                {images.length > 1 &&
                    <div className="challenge-page_grand-prize_carousel_arrow-left" onClick={handlePrevClick}>
                        <RxCaretLeft size={24} color="#FFFFFF"/>
                    </div>
                }
                {images.length > 1 &&
                    <div className="challenge-page_grand-prize_carousel_arrow-right" onClick={handleNextClick}>
                        <RxCaretRight size={24} color="#FFFFFF"/>
                    </div>
                }
                <img src={images[imageIndex]} alt={`Grand Prize ${imageIndex + 1}`} className="challenge-page_grand-prize_carousel_image"/>
                {images.length > 1 &&
                    <div className="challenge-page_carousel-dots" style={{ bottom: "10px" }}>
                        {images.map((_, idx) =>
                            <span
                                key={idx}
                                className={`challenge-page_carousel-dot ${idx === imageIndex ? "active" : ""}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setImageIndex(idx);
                                }}
                            />)}
                    </div>
                }
            </div>
            {lightboxOpen && images.length > 0 &&
                <ScreenshotLightbox
                    isOpen={lightboxOpen}
                    screenshots={images}
                    initialIndex={imageIndex}
                    onClose={() => setLightboxOpen(false)}
                />
            }
        </>
    );
};

interface GrandPrizeProps {
    segments?: ChallengeSegment[];
    challenge?: ChallengeInfoResponse;
}

export const GrandPrize = ({ segments, challenge }: GrandPrizeProps) => {
    // Cast to get grandPrize field (not yet in generated types)
    const extendedChallenge = challenge as ChallengeInfoResponse & {
        grandPrize?: {
            enabled?: boolean;
            prizePools?: Array<{ imageUrls?: string[]; rewardConfig?: { name?: string; description?: string } }>;
        };
    };

    // If grandPrize is explicitly disabled, don't show
    if (extendedChallenge?.grandPrize?.enabled === false) {
        return null;
    }

    // Find the Grand Finale segment as fallback (case-insensitive match)
    const grandFinaleSegment = segments?.find((s) => s.title?.toLowerCase().includes("grand finale") || s.title?.toLowerCase().includes("grand prize"));

    // Priority: Grand Prize prize pool images > segment prize pool images > segment reward images
    const grandPrizePrizePool = extendedChallenge?.grandPrize?.prizePools?.[0];
    const grandPrizePrizePoolImages = grandPrizePrizePool?.imageUrls?.length ? grandPrizePrizePool.imageUrls : [];

    const segmentPrizePool = grandFinaleSegment?.prizePools?.[0];
    const segmentPrizePoolImages = segmentPrizePool?.imageUrls?.length ? segmentPrizePool.imageUrls : [];

    const segmentRewardImages = grandFinaleSegment?.rewardImageUrls?.length ? grandFinaleSegment.rewardImageUrls : [];

    // Use first available image source
    const grandPrizeImages = grandPrizePrizePoolImages.length
        ? grandPrizePrizePoolImages
        : segmentPrizePoolImages.length
            ? segmentPrizePoolImages
            : segmentRewardImages;

    // Get prize name and description from prize pool - NO FALLBACKS (hide if no data)
    const prizeName = grandPrizePrizePool?.rewardConfig?.name || segmentPrizePool?.rewardConfig?.name;
    const prizeDescription = grandPrizePrizePool?.rewardConfig?.description || segmentPrizePool?.rewardConfig?.description;

    // Hide entire section if no prize data configured
    if (!prizeName && grandPrizeImages.length === 0) {
        return null;
    }

    return (
        <div className="challenge-page_grand-prize">
            {prizeName &&
                <div className="challenge-page_grand-prize_title">
                    {prizeName}
                </div>
            }
            {prizeDescription &&
                <div className="challenge-page_grand-prize_description">
                    {prizeDescription}
                </div>
            }
            <GrandPrizeCarousel images={grandPrizeImages}/>
        </div>
    );
};

interface QuestCardProps {
    milestone: QuestConfigMilestone;
    label: string;
    icon: React.ReactNode;
    type: "outlined" | "filled"
    isComplete: boolean;
    isClaimed: boolean;
    refetch: () => void;
    refetchChallenge: () => void;
    isAccepted: boolean;
    challenge: ChallengeInfoResponse;
    onLoginRequired: () => void;
    onReferralClick?: () => void;
    /** Current progress amount (for referrals) */
    currentAmount?: number;
    /** Target amount to complete (for referrals) */
    targetAmount?: number;
    /** Disable interactions (e.g., during segment gap) */
    isDisabled?: boolean;
    /** Type-specific card tracker colors from admin config */
    trackerColors?: CardTrackerColorConfig;
    /** Override brand name (used when challenge.brandName is empty, e.g., from Hub) */
    brandNameOverride?: string;
    /** Override brand color (used when challenge.brandColor is empty, e.g., from Hub) */
    brandColorOverride?: string;
}

const getUrlForMilestone = (milestone: QuestConfigMilestone) => {
    return milestone.url;
};

export const QuestCard = (props: QuestCardProps) => {
    const { label, icon, milestone, isComplete, isClaimed, refetch, refetchChallenge, isAccepted, challenge, onLoginRequired, onReferralClick, currentAmount, targetAmount, isDisabled, trackerColors, brandNameOverride, brandColorOverride } = props;
    const { fullToken } = useSelector((store: IStore) => store.authStore);
    const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
    const [showAcceptModal, setShowAcceptModal] = useState(false);
    const [isClaiming, setIsClaiming] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const milestoneUrl = getUrlForMilestone(milestone);
    const rewardConfig = milestone.rewardConfigs?.[0];
    const entries = rewardConfig?.amount;

    // Get platform info for expanded state
    const platformInfo = milestoneUrl ? getIconForUrl(milestoneUrl) : null;
    const isMobile = typeof window !== "undefined" && (/iPhone|iPad|iPod|Android/i).test(navigator.userAgent);

    // Determine card state: pending, claimable, or claimed
    const isClaimable = isComplete && !isClaimed;

    /*
     * Card styling from tracker colors (admin config) - applies inline styles
     * Falls back to CSS variables which inherit from challenge brand color (never hardcoded)
     */
    const cardStyle: React.CSSProperties = trackerColors ? {
        "--tracker-bg": trackerColors.backgroundColor || "transparent",
        "--tracker-border": trackerColors.borderColor || "var(--challenge-primary)",
        "--tracker-text": trackerColors.textColor || "var(--challenge-text-primary)",
        "--tracker-accent": trackerColors.accentColor || "var(--challenge-primary)",
        "--tracker-icon": trackerColors.iconColor || "var(--challenge-text-primary)",
    } as React.CSSProperties : {};

    // Accent color for entries and checkmarks - uses CSS variable (brand color), never hardcoded
    const accentColor = trackerColors?.accentColor || "var(--challenge-primary)";

    const renderEntries = () => {
        if (isClaimed) {
            // Claimed state: checkmark + entries (grayed out)
            return <><FaCheck size={18} color={accentColor} /> {entries}</>;
        }
        if (isClaimable) {
            // Claimable state: checkmark + "Claim XXX" button style
            return (
                <span className="challenge-page_quest-card_claim-button" style={{backgroundColor: accentColor}}>
                    <FaCheck size={14} /> Claim {entries}
                </span>
            );
        }
        // Pending state: + entries
        return `+ ${entries}`;
    };

    // For referral milestones, check if max referrals reached
    const isReferralComplete = milestone.subtype === MilestoneSubtype.REFERRAL &&
        targetAmount !== undefined && currentAmount !== undefined && currentAmount >= targetAmount;

    const inner = <>
        <div className="challenge-page_quest-card_left">
            {icon}
            <div className="challenge-page_quest-card_label">
                {label}
                {/* Show description for referral milestones if available */}
                {milestone.subtype === MilestoneSubtype.REFERRAL && milestone.description &&
                    <span className="challenge-page_quest-card_description">
                        {milestone.description}
                    </span>
                }
                {/* Show referral progress count */}
                {milestone.subtype === MilestoneSubtype.REFERRAL && targetAmount !== undefined &&
                    <span className="challenge-page_quest-card_progress">
                        {(currentAmount ?? 0) > 0
                            ? `${currentAmount}/${targetAmount} Earned`
                            : "Invite friends to earn entries!"}
                    </span>
                }
            </div>
        </div>
        <div className="challenge-page_quest-card_entries" style={{color: trackerColors?.textColor}}>
            {isReferralComplete
                ? <><FaCheck size={18} color={accentColor} /> {entries}</>
                : renderEntries()}
        </div>
    </>;

    // Handle claim action
    const handleClaim = async () => {
        if (!fullToken || isClaiming) return;
        setIsClaiming(true);
        try {
            await new QuestsApi(getConfig(fullToken)).createQuestConfigsIdClaimReward({
                id: milestone.id,
            });
            refetch();
        } catch (err) {
            console.error("Failed to claim reward:", err);
        } finally {
            setIsClaiming(false);
        }
    };

    // Handle click - check login and acceptance first
    const handleClick = async () => {
        // Block interactions during segment gap (no active weekly segment)
        if (isDisabled) {
            return;
        }

        if (!fullToken) {
            onLoginRequired();
            return;
        }
        if (!isAccepted) {
            setShowAcceptModal(true);
            return;
        }

        // If claimable, trigger claim instead of opening URL
        if (isClaimable) {
            await handleClaim();
            return;
        }

        // If already claimed, do nothing
        if (isClaimed) {
            return;
        }

        // User is logged in and has accepted - perform normal action (pending state)
        if (milestoneUrl) {
            // Expand card instead of opening link directly
            setIsExpanded(true);
        } else if (milestone.subtype === MilestoneSubtype.REFERRAL && onReferralClick) {
            // Referral milestones open the invite modal
            onReferralClick();
        } else {
            handlePhotoMilestoneClick();
        }
    };

    const handleUrlClick = async (openInApp: boolean = true) => {
        // Log the click event
        await logClickUrlEvent(milestoneUrl, true, true);
        if (openInApp) {
            /*
             * Try deep link without fallback navigation - prevents navigating away from campaign
             * If app doesn't open, user can use "Open in Browser" instead
             */
            const { platform, handle } = detectPlatformFromUrl(milestoneUrl);
            const deepLink = handle ? generateDeepLink(platform, handle) : null;
            if (deepLink) {
                const anchor = document.createElement("a");
                anchor.href = deepLink;
                anchor.style.display = "none";
                document.body.appendChild(anchor);
                anchor.click();
                document.body.removeChild(anchor);
            } else {
                // No deep link available, open in new tab instead
                window.open(milestoneUrl, "_blank", "noopener,noreferrer");
            }
        } else {
            // Force browser open in new tab
            window.open(milestoneUrl, "_blank", "noopener,noreferrer");
        }
        setIsExpanded(false);
    };

    const handleCollapseCard = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsExpanded(false);
    };

    const handlePhotoMilestoneClick = () => {
        setIsPhotoModalOpen(true);
    };

    // Determine CSS class based on state
    const getStateClass = () => {
        const classes: string[] = [];
        if (isClaimed) classes.push("claimed");
        if (isClaimable) classes.push("claimable");
        if (isDisabled) classes.push("disabled");
        return classes.join(" ");
    };

    // URL-based milestones get outlined style with border
    if (milestoneUrl) {
        const PlatformIcon = platformInfo?.icon;
        const platformName = platformInfo ? getPlatformConfig(platformInfo.platform).name : "Link";

        // Check if this URL has a valid deep link (for "Open in App" button)
        const { platform, handle } = detectPlatformFromUrl(milestoneUrl);
        const hasDeepLink = Boolean(handle && generateDeepLink(platform, handle));

        return (
            <>
                <ChallengeAcceptModal
                    challenge={challenge}
                    isVisible={showAcceptModal}
                    onClose={() => setShowAcceptModal(false)}
                    onAccepted={refetchChallenge}
                    showNewsletterFields={true}
                    brandNameOverride={brandNameOverride}
                    brandColorOverride={brandColorOverride}
                />
                <div
                    className={`challenge-page_orange-border ${getStateClass()} ${isExpanded ? "expanded" : ""}`}
                    onClick={isExpanded ? undefined : handleClick}
                    style={{
                        ...cardStyle,
                        borderColor: trackerColors?.borderColor,
                    }}
                >
                    <div
                        className={`challenge-page_quest-card url-variant ${getStateClass()} ${isExpanded ? "expanded" : ""}`}
                        style={{
                            backgroundColor: trackerColors?.backgroundColor,
                            color: trackerColors?.textColor,
                        }}
                    >
                        {!isExpanded
                            ? inner
                            : <div className="challenge-page_quest-card_expanded">
                                <div className="challenge-page_quest-card_expanded_header">
                                    {PlatformIcon && <PlatformIcon size={32} color={platformInfo?.color} />}
                                    <div className="challenge-page_quest-card_expanded_title">
                                        {label}
                                    </div>
                                </div>
                                <div className="challenge-page_quest-card_expanded_subtitle">
                                    Earn +{entries} entries
                                </div>
                                <div className="challenge-page_quest-card_expanded_buttons">
                                    {isMobile && hasDeepLink &&
                                        <button
                                            className="challenge-page_quest-card_expanded_button primary"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleUrlClick(true);
                                            }}
                                        >
                                            Open in {platformName} App
                                        </button>
                                    }
                                    <button
                                        className={`challenge-page_quest-card_expanded_button ${isMobile && hasDeepLink ? "secondary" : "primary"}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleUrlClick(false);
                                        }}
                                    >
                                        {isMobile && hasDeepLink ? "Open in Browser" : "Open Link"}
                                    </button>
                                    <button
                                        className="challenge-page_quest-card_expanded_button cancel"
                                        onClick={handleCollapseCard}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </>
        );
    }

    // REFERRAL milestones get outlined style (same as social/URL cards) but no URL expand behavior
    if (milestone.subtype === MilestoneSubtype.REFERRAL) {
        return (
            <>
                <ChallengeAcceptModal
                    challenge={challenge}
                    isVisible={showAcceptModal}
                    onClose={() => setShowAcceptModal(false)}
                    onAccepted={refetch}
                    showNewsletterFields={true}
                    brandNameOverride={brandNameOverride}
                    brandColorOverride={brandColorOverride}
                />
                <div
                    className={`challenge-page_orange-border ${getStateClass()}`}
                    onClick={handleClick}
                    style={{
                        ...cardStyle,
                        borderColor: trackerColors?.borderColor,
                    }}
                >
                    <div
                        className={`challenge-page_quest-card url-variant ${getStateClass()}`}
                        style={{
                            backgroundColor: trackerColors?.backgroundColor,
                            color: trackerColors?.textColor,
                        }}
                    >
                        {inner}
                    </div>
                </div>
            </>
        );
    }

    // Photo milestones get filled style
    return (
        <>
            <ChallengeAcceptModal
                challenge={challenge}
                isVisible={showAcceptModal}
                onClose={() => setShowAcceptModal(false)}
                onAccepted={refetch}
                showNewsletterFields={true}
                brandNameOverride={brandNameOverride}
                brandColorOverride={brandColorOverride}
            />
            <PhotoModal
                photoType={milestone.triggerEvent?.photoType as PhotoType}
                isOpen={isPhotoModalOpen}
                onClose={() => setIsPhotoModalOpen(false)}
                onSubmit={refetch}
                entriesEarned={milestone.rewardConfigs?.[0]?.amount}
                milestone={milestone}
                refetch={refetch}
            />
            <div
                className={`challenge-page_quest-card_filled ${getStateClass()}`}
                onClick={handleClick}
            >
                {inner}
            </div>
        </>
    );
};

// GameQuestSection - Displays game milestones grouped by game with progress tracking
interface GameQuestSectionProps {
    gameId: string;
    milestones: QuestConfigMilestone[];
    progressionMap: Record<string, { amount?: number; isComplete?: boolean; claimedAt?: number }>;
    trackerColors?: CardTrackerColorConfig;
    refetch: () => void;
    isAccepted: boolean;
    challenge: ChallengeInfoResponse;
    onLoginRequired: () => void;
}

export const GameQuestSection = ({
    gameId,
    milestones,
    progressionMap,
    trackerColors,
    refetch,
    isAccepted,
    challenge,
    onLoginRequired,
}: GameQuestSectionProps) => {
    const { data: game } = useGetOverwolfGameInfo(gameId);
    const { data: bannerImageUrl } = useGetAsset(game?.bannerImageLarge);
    const { data: logoImageUrl } = useGetAsset(game?.logo);

    // Card styling from tracker colors (admin config)
    const sectionStyle: React.CSSProperties = trackerColors ? {
        "--tracker-bg": trackerColors.backgroundColor || "var(--challenge-card-background)",
        "--tracker-border": trackerColors.borderColor || "var(--challenge-primary)",
        "--tracker-text": trackerColors.textColor || "var(--challenge-text-primary)",
        "--tracker-accent": trackerColors.accentColor || "var(--challenge-primary)",
    } as React.CSSProperties : {};

    return (
        <div className="brand-challenge_game" style={sectionStyle}>
            <div className="brand-challenge_game_header">
                {bannerImageUrl &&
                    <img
                        src={bannerImageUrl.url}
                        alt={game?.name}
                        className="brand-challenge_game_header_bg"
                    />
                }
                {!gameId || gameId === "other"
                    ? <img
                        src={`${import.meta.env.VITE_CDN_URL}/images/ow-time-govip-bg.webp`}
                        alt="Other Games"
                        className="brand-challenge_game_header_bg"
                    />
                    : null}
                <div className="brand-challenge_game_header_content">
                    {gameId && gameId !== "other"
                        ? <>
                            <img src={logoImageUrl?.url} alt={game?.name} />
                            <div>Complete {game?.name || "Game"} Quests to Earn Beast Points!</div>
                        </>
                        : <>
                            <h3>OTHER GAMES</h3>
                            <div>Complete Quests to Earn Beast Points!</div>
                        </>
                    }
                </div>
            </div>
            <div className="brand-challenge_game_body">
                {milestones.map((milestone) => {
                    const progression = progressionMap[milestone.id];
                    const currentProgress = progression?.amount || 0;
                    const targetAmount = milestone.targetAmount || 1;
                    const isComplete = progression?.isComplete || currentProgress >= targetAmount;
                    const isClaimed = !!(progression?.isComplete && progression?.claimedAt);
                    const rewardConfig = milestone.rewardConfigs?.[0];
                    const rewardAmount = rewardConfig?.amount;

                    return (
                        <div key={milestone.id} className="brand-challenge_quest">
                            <CircularProgressIndicator
                                showGlow={isComplete}
                                total={targetAmount}
                                current={currentProgress}
                            />
                            <div className="brand-challenge_quest_info">
                                <strong className="brand-challenge_quest_name">
                                    {milestone.name || "Complete the Quest"}
                                </strong>
                                <div className="brand-challenge_quest_description">
                                    {milestone.description}
                                </div>
                                {isComplete && !isClaimed &&
                                    <div className="brand-challenge_quest_claimable">
                                        Ready to Claim!
                                    </div>
                                }
                                {isClaimed &&
                                    <div className="brand-challenge_quest_claimed">
                                        <FaCheck size={12} /> Claimed
                                    </div>
                                }
                            </div>
                            {rewardAmount &&
                                <div className="brand-challenge_quest_reward">
                                    +{rewardAmount}
                                </div>
                            }
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// HowToPlay component moved to ChallengeHowToPlay.tsx

interface SegmentPrizeDisplayProps {
    isCurrentSegment: boolean;
    prizeName: string;
    prizeDescription?: string;
    segmentName: string;
    prizeImageUrl?: string;
    prizeImageUrls?: string[]; // Multiple images for carousel
}

const SegmentLabelCard = (props: SegmentPrizeDisplayProps) => {
    const { isCurrentSegment, segmentName } = props;
    if (isCurrentSegment) {
        return (
            <div className="challenge-page_segment-label-card_filled">
                <div className="challenge-page_segment-label-card_title">
                    {segmentName} <div className="challenge-page_green-dot"/>
                </div>
            </div>
        );
    }
    return (
        <div className="challenge-page_segment-label-card">
            <div className="challenge-page_segment-label-card_title">
                {segmentName}
            </div>
        </div>
    );
};

export const SegmentPrizeDisplay = (props: SegmentPrizeDisplayProps) => {
    const { prizeImageUrl, prizeImageUrls, prizeDescription, prizeName } = props;
    const [currentIndex, setCurrentIndex] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);

    // Use array if available, otherwise fall back to single image
    const images = prizeImageUrls?.length ? prizeImageUrls : prizeImageUrl ? [prizeImageUrl] : [];
    const hasMultipleImages = images.length > 1;

    const handlePrev = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentIndex((prev) => prev === 0 ? images.length - 1 : prev - 1);
    };

    const handleNext = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentIndex((prev) => prev === images.length - 1 ? 0 : prev + 1);
    };

    return (
        <div className="challenge-page_segment-prize-display">
            <SegmentLabelCard {...props}/>
            <div
                className="challenge-page_segment-prize-display_image-container"
                onClick={() => setLightboxOpen(true)}
                style={{ cursor: "pointer", position: "relative" }}
            >
                <img
                    src={images[currentIndex]}
                    alt="Segment Prize"
                    className="challenge-page_segment-prize-display_image"
                />
                {hasMultipleImages &&
                    <>
                        <button
                            className="challenge-page_carousel-nav challenge-page_carousel-nav--prev"
                            onClick={handlePrev}
                            aria-label="Previous image"
                        >
                            <FaChevronLeft />
                        </button>
                        <button
                            className="challenge-page_carousel-nav challenge-page_carousel-nav--next"
                            onClick={handleNext}
                            aria-label="Next image"
                        >
                            <FaChevronRight />
                        </button>
                        <div className="challenge-page_carousel-dots">
                            {images.map((_, idx) =>
                                <span
                                    key={idx}
                                    className={`challenge-page_carousel-dot ${idx === currentIndex ? "active" : ""}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCurrentIndex(idx);
                                    }}
                                />)}
                        </div>
                    </>
                }
            </div>
            <ScreenshotLightbox
                isOpen={lightboxOpen}
                screenshots={images}
                initialIndex={currentIndex}
                onClose={() => setLightboxOpen(false)}
                title={prizeName}
                description={prizeDescription}
            />
        </div>
    );
};

interface SegmentPrizeGridProps {
    segments?: ChallengeSegment[];
}

const SegmentPrizeGrid = (props: SegmentPrizeGridProps) => {
    const { segments } = props;

    // Filter to only show segments with displayTab enabled
    const visibleSegments = segments?.filter(segment => segment.displayTab);

    // Sort segments: active first, then future, then past (by start time within each group)
    const sortedSegments = visibleSegments?.slice().sort((a, b) => {
        const now = Date.now();
        const aStart = a.startAt;
        const bStart = b.startAt;
        const aEnd = a.endAt;
        const bEnd = b.endAt;

        const aIsActive = now >= aStart && now <= aEnd;
        const bIsActive = now >= bStart && now <= bEnd;
        const aIsPast = now > aEnd;
        const bIsPast = now > bEnd;

        // Active segments come first
        if (aIsActive && !bIsActive) return -1;
        if (!aIsActive && bIsActive) return 1;

        // Past segments go to the end
        if (aIsPast && !bIsPast) return 1;
        if (!aIsPast && bIsPast) return -1;

        // Within same category, sort by start time
        return aStart - bStart;
    });

    return (
        <div className="challenge-page_segment-prize-grid">
            {sortedSegments?.map((segment, index) => {
                const startDate = new Date(segment.startAt);
                const endAt = new Date(segment.endAt);
                const now = new Date();
                const isCurrentSegment = now >= startDate && now <= endAt;

                // Priority: Prize pool images > Segment images
                const prizePool = segment.prizePools?.[0];
                const prizePoolImages = prizePool?.imageUrls?.length ? prizePool.imageUrls : [];
                const segmentImages = segment.rewardImageUrls?.length ? segment.rewardImageUrls : segment.rewardImage?.url ? [segment.rewardImage.url] : [];
                const images = prizePoolImages.length ? prizePoolImages : segmentImages;

                return (
                    <SegmentPrizeDisplay
                        key={index}
                        prizeName={prizePool?.rewardConfig?.name || "Not found"}
                        prizeDescription={prizePool?.rewardConfig?.description}
                        isCurrentSegment={isCurrentSegment}
                        segmentName={segment.title}
                        prizeImageUrls={images}
                    />
                );
            })}
        </div>
    );
};

interface EnterNowButtonProps {
    challenge?: ChallengeInfoResponse & { start?: number; end?: number };
    refetch: () => void;
    onLoginRequired: () => void;
}

export const EnterNowButton = ({ challenge, refetch, onLoginRequired }: EnterNowButtonProps) => {
    const { fullToken } = useSelector((store: IStore) => store.authStore);
    const { mutateAsync: submitChallengeResponse } = useSubmitChallengeResponse();
    const [showAcceptModal, setShowAcceptModal] = useState(false);

    // Don't show button if already accepted
    if (challenge?.isAccepted) {
        return null;
    }

    // Don't show button if challenge has ended
    const segments = challenge?.segments ?? [];
    const latestEndTime = segments.reduce((max, seg) => Math.max(max, seg.endAt), 0);
    if (challenge?.hasEnded || Date.now() > latestEndTime) {
        return null;
    }

    const handleClick = () => {
        if (!fullToken) {
            onLoginRequired();
        } else {
            // Logged in - show accept modal
            setShowAcceptModal(true);
        }
    };

    const handleAccepted = async (newsletterOptIn?: boolean, zipCode?: string) => {
        if (fullToken && challenge?.id) {
            try {
                await submitChallengeResponse({
                    id: challenge.id,
                    newsletter: newsletterOptIn,
                    zipCode: zipCode || undefined,
                });
            } catch (err) {
                console.error("Failed to submit challenge response:", err);
            }
        }
        refetch();
    };

    return (
        <>
            <ChallengeAcceptModal
                challenge={challenge as any}
                isVisible={showAcceptModal}
                onAccepted={handleAccepted}
                onClose={() => setShowAcceptModal(false)}
                showNewsletterFields={true}
            />
            <button className="challenge-page_enter-now-button" onClick={handleClick}>
                ENTER NOW
            </button>
        </>
    );
};

/*
 * ProductInfo, ExploreProducts, FaqItem, and FrequentlyAskedQuestions components removed
 * Now using ChallengePromotionsSection and ChallengeFAQ
 */

interface ChallengePageProps {
    challengeId?: string;
    utmSource?: string;
    challengeData?: ChallengeInfoResponse;
    onRefetch?: () => void;
}

export const ChallengePage = ({
    challengeId: propsChallengeId,
    utmSource: propsUtmSource,
    challengeData: propsChallengeData,
    onRefetch: propsOnRefetch,
}: ChallengePageProps = {}) => {
    const { id: routeChallengeId } = useParams<{ id: string }>();
    const challengeId = propsChallengeData?.id || propsChallengeId || routeChallengeId;
    const utmSource = propsUtmSource;

    const dispatch = useDispatch();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const { mutateAsync: submitChallengeResponse } = useSubmitChallengeResponse();
    const [isChallengeLoginOpen, setIsChallengeLoginOpen] = useState(false);
    const [showAcceptModalAfterLogin, setShowAcceptModalAfterLogin] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [segmentTick, setSegmentTick] = useState(0); // Triggers recalculation when segments change
    const handleLoginRequired = () => setIsChallengeLoginOpen(true);
    const handleInviteClick = () => setShowInviteModal(true);
    const {data: partnerService} = useGetPartnerService(utmSource);
    const referralCode = partnerService?.referralCode;

    useEffect(() => {
        if (!fullToken && referralCode) {
            // If the user is not logged in, store the referral code in redux until they log in or sign up.
            dispatch(newReferralCode(referralCode));
        }
    }, [fullToken?.id, referralCode]);

    const handleLoginSuccess = () => {
        // Flag to show Accept Challenge modal after login completes
        setShowAcceptModalAfterLogin(true);
    };

    // Handle challenge acceptance with newsletter/zip submission
    const handleChallengeAccepted = async (newsletterOptIn: boolean, zipCode?: string) => {
        // Submit the newsletter/zip response
        if (fullToken) {
            try {
                await submitChallengeResponse({
                    id: challengeId,
                    newsletter: newsletterOptIn,
                    zipCode: zipCode || undefined,
                });
            } catch (err) {
                console.error("Failed to submit challenge response:", err);
            }
        }
        refetch();
    };

    const handleLoginClose = () => {
        setIsChallengeLoginOpen(false);
        /*
         * Force reset overflow when login modal closes (especially after auto-close on successful login)
         * This prevents scroll lock if modal count gets out of sync
         */
        setTimeout(() => {
            document.body.style.overflow = "";
            document.body.style.paddingRight = "0";
        }, 300); // Small delay to let modal animation complete
    };

    /*
     * Cleanup: Force reset body overflow when this page unmounts
     * This handles case where photo modals are pre-rendered and cause activeModals count to get out of sync
     */
    useEffect(() => {
        return () => {
            document.body.style.overflow = "";
            document.body.style.paddingRight = "0";
        };
    }, []);

    // Use pre-fetched data if provided, otherwise fetch by ID
    const {
        data: fetchedChallenge,
        refetch: fetchRefetch,
    } = useGetChallenge(propsChallengeData ? undefined : challengeId);

    // Prefer pre-fetched data when available
    const challenge = propsChallengeData || fetchedChallenge;
    const refetch = propsOnRefetch || fetchRefetch;

    // Cast to extended type for SEO config access
    const extendedChallenge = challenge as ExtendedChallengeInfo | undefined;

    // Fetch SEO assets (social share image and favicon)
    const { data: socialShareAsset } = useGetAsset(extendedChallenge?.seo?.socialShareImageId || "");
    const { data: faviconAsset } = useGetAsset(extendedChallenge?.seo?.faviconId || "");

    // Build SEO values from challenge config, with sensible defaults
    const seoTitle = extendedChallenge?.seo?.title || extendedChallenge?.name || "Challenge";
    const seoDescription = extendedChallenge?.seo?.description || extendedChallenge?.description || "";
    const seoCanonicalUrl = extendedChallenge?.seo?.canonicalUrl || (typeof window !== "undefined" ? window.location.href : "");
    const seoImageUrl = socialShareAsset?.url || socialShareAsset?.pipeUrl || "https://cdn.devourplay.gg/images/social-preview.png";
    const seoFavicon = faviconAsset?.url || faviconAsset?.pipeUrl || undefined;

    // Get current segment info - used for both title AND milestones to ensure consistency
    const { displayCurrentSegment, activeSegments } = useMemo(() => {
        const segments = challenge?.segments ?? [];
        const now = new Date();

        // Filter to active segments (same logic used everywhere)
        const active = segments.filter(segment => {
            const startDate = new Date(segment.startAt);
            const endDate = new Date(segment.endAt);
            return now >= startDate && now <= endDate;
        });

        // For the title, use the current segment that has displayTab: true (weekly segments)
        const displaySegment = active.find(segment => segment.displayTab);

        return {
            displayCurrentSegment: displaySegment,
            activeSegments: active,
        };
    // segmentTick forces recalculation when segment transitions occur
    }, [challenge?.segments, segmentTick]);

    useWebsocket(WebsocketEvent.QUEST_PROGRESSED, () => {
        refetch?.();
    });

    // Auto-refresh when segment transitions (ends or new one starts)
    useEffect(() => {
        if (!challenge?.segments?.length) return;

        const segments = challenge.segments;
        const now = Date.now();

        // Find the current weekly segment (displayTab: true)
        const currentWeeklySegment = segments.find(segment => {
            return segment.displayTab && now >= segment.startAt && now <= segment.endAt;
        });

        // Find the next segment that will start
        const nextSegment = segments
            .filter(s => s.displayTab && s.startAt > now)
            .sort((a, b) => a.startAt - b.startAt)[0];

        // Calculate time until current segment ends or next segment starts
        const timeUntilCurrentEnds = currentWeeklySegment
            ? currentWeeklySegment.endAt - now
            : Infinity;
        const timeUntilNextStarts = nextSegment
            ? nextSegment.startAt - now
            : Infinity;

        // Use whichever transition comes first (minimum of the two)
        const timeUntilTransition = Math.min(timeUntilCurrentEnds, timeUntilNextStarts);

        // Only set timer if transition is within 24 hours (to avoid very long timers)
        if (timeUntilTransition > 0 && timeUntilTransition < 24 * 60 * 60 * 1000) {
            const timer = setTimeout(async () => {
                await refetch?.(); // Wait for fresh data
                setSegmentTick(t => t + 1); // Then trigger useMemo recalculation
            }, timeUntilTransition + 2000); // 2 second buffer for network latency

            return () => clearTimeout(timer);
        }
    }, [challenge?.segments, refetch]);

    // Get past segments (already ended)
    const pastSegments = useMemo(() => {
        const segments = challenge?.segments ?? [];
        return segments.filter(segment => {
            const now = new Date();
            const endDate = new Date(segment.endAt);
            return now > endDate;
        });
    // segmentTick forces recalculation when segment transitions occur
    }, [challenge?.segments, segmentTick]);

    // Build a map of progressions by milestone ID from ALL segments (not just active)
    const progressionMap = useMemo(() => {
        const map: Record<string, { amount?: number; isComplete?: boolean; claimedAt?: number }> = {};
        const allSegments = challenge?.segments ?? [];
        allSegments.forEach(segment => {
            const progression = challenge?.progressions?.find(p => p.questConfigId === segment.mainQuestId);
            if (progression?.progression) {
                Object.entries(progression.progression).forEach(([milestoneId, data]) => {
                    map[milestoneId] = data;
                });
            }
        });
        return map;
    }, [challenge?.segments, challenge?.progressions]);

    // Combine milestones: all from active segments + CLAIMED ones from past segments
    const currentMilestones = useMemo(() => {
        // All milestones from active segments (show all, completed or not)
        const activeMilestones = activeSegments.flatMap(segment => segment.mainQuest?.milestones ?? []);

        // Only CLAIMED milestones from past segments
        const claimedPastMilestones = pastSegments.flatMap(segment => {
            const milestones = segment.mainQuest?.milestones ?? [];
            return milestones.filter(m => {
                const prog = progressionMap[m.id];
                return prog?.isComplete && prog?.claimedAt;
            });
        });

        // Deduplicate by milestone ID
        const seen = new Set<string>();
        return [...activeMilestones, ...claimedPastMilestones].filter(m => {
            if (seen.has(m.id)) return false;
            seen.add(m.id);
            return true;
        });
    }, [activeSegments, pastSegments, progressionMap]);

    // Separate milestones by subtype: PHOTO, SOCIAL, GAME, REFERRAL
    const { photoMilestones, socialMilestones, gameMilestonesByGame } = useMemo(() => {
        const photo: QuestConfigMilestone[] = [];
        const social: QuestConfigMilestone[] = [];
        const game: Record<string, QuestConfigMilestone[]> = {};
        const referral: QuestConfigMilestone[] = [];

        for (const milestone of currentMilestones) {
            const subtype = milestone.subtype;

            if (subtype === MilestoneSubtype.PHOTO) {
                photo.push(milestone);
            } else if (subtype === MilestoneSubtype.SOCIAL) {
                social.push(milestone);
            } else if (subtype === MilestoneSubtype.GAME) {
                // Group by gameId
                const gameId = milestone.gameId || "other";
                if (!game[gameId]) {
                    game[gameId] = [];
                }
                game[gameId].push(milestone);
            } else if (subtype === MilestoneSubtype.REFERRAL) {
                referral.push(milestone);
            } else if (!subtype) {
                // Legacy: determine by URL presence if no subtype set
                if (getUrlForMilestone(milestone)) {
                    social.push(milestone);
                } else {
                    // Default to photo for legacy milestones without URL
                    photo.push(milestone);
                }
            }
        }

        return {
            photoMilestones: photo,
            socialMilestones: social,
            gameMilestonesByGame: game,
            referralMilestones: referral,
        };
    }, [currentMilestones]);

    // Check if we have any game milestones
    const hasGameMilestones = Object.keys(gameMilestonesByGame).length > 0;


    /*
     * Derive start/end from segments if not on challenge directly
     * TODO: Challenge schema should have start/end fields - currently only segments have startAt/endAt
     */
    const challengeWithDates = useMemo(() => {
        if (!challenge) return challenge;
        // Check if challenge already has start/end (may exist in DB but not in TypeScript types)
        const existingStart = (challenge as any).start;
        const existingEnd = (challenge as any).end;
        if (existingStart && existingEnd) {
            return { ...challenge, start: existingStart, end: existingEnd };
        }
        // Fallback: derive from segments
        if (!challenge.segments?.length) return challenge;
        const starts = challenge.segments.map(s => s.startAt).filter(Boolean);
        const ends = challenge.segments.map(s => s.endAt).filter(Boolean);
        if (!starts.length || !ends.length) return challenge;
        return {
            ...challenge,
            start: Math.min(...starts),
            end: Math.max(...ends),
        };
    }, [challenge]);

    if (!challenge) {
        return Skeleton({height: "100vh", width: "100vw"});
    }

    /*
     * Extract theme colors and build CSS variables
     * Priority: themeColors (Page Design) > brandColor (from challenge/brand) > NO default (let component handle)
     */
    const themeColors = challenge?.themeColors;
    const brandColor = challenge?.brandColor;

    // Helper to check if a color value is actually set (not empty string or undefined)
    const hasValue = (val: string | undefined): val is string => Boolean(val && val.trim());

    /*
     * Always set CSS variables - use themeColors > brandColor
     * brandColor is set at the Brand level and should be the ultimate source of truth
     */
    const primaryColor = hasValue(themeColors?.primary) ? themeColors.primary : hasValue(brandColor) ? brandColor : undefined;
    const secondaryColor = hasValue(themeColors?.secondary) ? themeColors.secondary : hasValue(brandColor) ? brandColor : undefined;

    const cssVariables: React.CSSProperties = {
        /*
         * Only set CSS variables if we have actual values
         * This allows SCSS fallbacks to kick in only when truly no color is configured
         */
        ...primaryColor && {"--challenge-primary": primaryColor},
        ...(themeColors?.primaryDark || primaryColor) && {"--challenge-primary-dark": themeColors?.primaryDark || primaryColor},
        ...secondaryColor && {"--challenge-secondary": secondaryColor},
        ...(themeColors?.secondaryDark || secondaryColor) && {"--challenge-secondary-dark": themeColors?.secondaryDark || secondaryColor},
        ...(themeColors?.accent || primaryColor) && {"--challenge-accent": themeColors?.accent || primaryColor},
        ...hasValue(themeColors?.background) && {"--challenge-background": themeColors.background},
        ...hasValue(themeColors?.cardBackground) && {"--challenge-card-background": themeColors.cardBackground},
        ...hasValue(themeColors?.textPrimary) && {"--challenge-text-primary": themeColors.textPrimary},
        ...hasValue(themeColors?.textSecondary) && {"--challenge-text-secondary": themeColors.textSecondary},
        ...hasValue(themeColors?.success) && {"--challenge-success": themeColors.success},
        ...hasValue(themeColors?.error) && {"--challenge-error": themeColors.error},
    } as React.CSSProperties;

    return (
        <div className="challenge-page" style={cssVariables}>
            <SEO
                title={seoTitle}
                description={seoDescription}
                url={seoCanonicalUrl}
                imageUrl={seoImageUrl}
                favicon={seoFavicon}
            />
            {/* Challenge-specific login modal with T&Cs checkboxes */}
            <ChallengeLoginModal
                challenge={challenge}
                isOpen={isChallengeLoginOpen}
                onClose={handleLoginClose}
                onLoginSuccess={handleLoginSuccess}
                title="Sign in to continue"
                description="To track your challenge progress, you will need a DevourPlay account. Sign-up or Login below."
                referralCode={referralCode}
            />
            {/* Accept Challenge modal - shown after login if user hasn't accepted yet */}
            <ChallengeAcceptModal
                challenge={challengeWithDates}
                isVisible={showAcceptModalAfterLogin && !!fullToken && !challenge.isAccepted}
                onClose={() => setShowAcceptModalAfterLogin(false)}
                onAccepted={handleChallengeAccepted}
                showNewsletterFields={true}
            />
            {/* Prize won notification modal */}
            <BrandChallengesPrizeNotifier />
            {/* Invite modal */}
            {challenge &&
                <InviteModal
                    type={InviteModalType.CHALLENGE}
                    challengeId={challenge.id}
                    isVisible={showInviteModal}
                    onClose={() => setShowInviteModal(false)}
                    alwaysUseDarkBackground={true}
                />
            }
            <ChallengeNavbar challenge={challengeWithDates as any} onLoginRequired={handleLoginRequired} onInviteClick={handleInviteClick} />

            {/* Pre-challenge countdown */}
            {challengeWithDates?.start && challengeWithDates.start > Date.now() &&
                <BrandChallengeCountdown challengeStart={challengeWithDates.start} />
            }

            <ChallengeHero challenge={challengeWithDates as any} refetch={refetch} onLoginRequired={handleLoginRequired}/>
            <div className="challenge-page_main-content">
                <ChallengeHowToPlay challenge={challenge} />
                <div className="challenge-page_prize-info">
                    <GrandPrize segments={challenge?.segments} challenge={challenge}/>
                    <TimeLeft challenge={challenge}/>
                    <SegmentPrizeGrid segments={challenge?.segments}/>
                </div>

                <div className="challenge-page_quest-entry-info">
                    <MyWeeklyEntries challenge={challenge} segmentTitle={displayCurrentSegment?.title}/>
                    <div className="challenge-page_quest-entry-info_text">
                        <div className="challenge-page_quest-entry-info_text_title">
                        YOUR {displayCurrentSegment?.title?.toUpperCase() || ""} CHALLENGES
                        </div>
                    </div>
                    {/* Photo quests - full width layout, claimed items go to bottom */}
                    {photoMilestones.length > 0 &&
                        <div className="challenge-page_quest-entry-info_rows">
                            {photoMilestones
                                .sort((a, b) => {
                                    // Only claimed items go to bottom (complete + has claimedAt)
                                    const aClaimed = progressionMap[a.id]?.isComplete && progressionMap[a.id]?.claimedAt ? 1 : 0;
                                    const bClaimed = progressionMap[b.id]?.isComplete && progressionMap[b.id]?.claimedAt ? 1 : 0;
                                    return aClaimed - bClaimed;
                                })
                                .map((milestone, index) => {
                                    const progression = progressionMap[milestone.id];
                                    // Get PHOTO tracker colors from questsStyles
                                    const photoTrackerColors = (challenge as ChallengeWithEntriesStyles)?.questsStyles?.cardTrackers?.find(t => t.subtype === "PHOTO");
                                    return <QuestCard
                                        refetch={refetch}
                                        refetchChallenge={refetch}
                                        key={index}
                                        label={milestone.name || "Complete the Challenge"}
                                        icon={<IoIosCamera size={48} />}
                                        type="filled"
                                        milestone={milestone}
                                        isComplete={progression?.isComplete ?? false}
                                        isClaimed={!!(progression?.isComplete && progression?.claimedAt)}
                                        isAccepted={challenge?.isAccepted ?? false}
                                        challenge={challengeWithDates}
                                        onLoginRequired={handleLoginRequired}
                                        currentAmount={progression?.amount ?? 0}
                                        targetAmount={milestone.targetAmount ?? 1}
                                        trackerColors={photoTrackerColors}
                                    />;
                                })}
                        </div>
                    }

                    {/* Social quests - 2 column layout, claimed items go to bottom */}
                    {socialMilestones.length > 0 &&
                        <div className="challenge-page_quest-entry-info_columns">
                            {socialMilestones
                                .sort((a, b) => {
                                    // Only claimed items go to bottom (complete + has claimedAt)
                                    const aClaimed = progressionMap[a.id]?.isComplete && progressionMap[a.id]?.claimedAt ? 1 : 0;
                                    const bClaimed = progressionMap[b.id]?.isComplete && progressionMap[b.id]?.claimedAt ? 1 : 0;
                                    return aClaimed - bClaimed;
                                })
                                .map((milestone, index) => {
                                    const progression = progressionMap[milestone.id];
                                    const milestoneUrl = milestone.url || "";
                                    const { icon: PlatformIcon } = getIconForUrl(milestoneUrl);
                                    // Get SOCIAL tracker colors from questsStyles
                                    const socialTrackerColors = (challenge as ChallengeWithEntriesStyles)?.questsStyles?.cardTrackers?.find(t => t.subtype === "SOCIAL");
                                    return <QuestCard
                                        refetch={refetch}
                                        refetchChallenge={refetch}
                                        key={index}
                                        label={milestone.name || "Complete the Challenge"}
                                        icon={<PlatformIcon size={24} />}
                                        type="outlined"
                                        milestone={milestone}
                                        isComplete={progression?.isComplete ?? false}
                                        isClaimed={!!(progression?.isComplete && progression?.claimedAt)}
                                        isAccepted={challenge?.isAccepted ?? false}
                                        challenge={challengeWithDates}
                                        onLoginRequired={handleLoginRequired}
                                        currentAmount={progression?.amount ?? 0}
                                        targetAmount={milestone.targetAmount ?? 1}
                                        trackerColors={socialTrackerColors}
                                    />;
                                })}
                        </div>
                    }

                    {/* Game quests - grouped by game with progress tracking */}
                    {hasGameMilestones &&
                        <div className="challenge-page_game-quests">
                            {Object.entries(gameMilestonesByGame).map(([gameId, milestones]) => {
                                // Get GAME tracker colors from questsStyles
                                const gameTrackerColors = (challenge as ChallengeWithEntriesStyles)?.questsStyles?.cardTrackers?.find(t => t.subtype === "GAME");
                                return (
                                    <GameQuestSection
                                        key={gameId}
                                        gameId={gameId}
                                        milestones={milestones}
                                        progressionMap={progressionMap as any}
                                        trackerColors={gameTrackerColors}
                                        refetch={refetch}
                                        isAccepted={challenge?.isAccepted ?? false}
                                        challenge={challenge}
                                        onLoginRequired={handleLoginRequired}
                                    />
                                );
                            })}
                        </div>
                    }
                </div>
            </div>
            <ChallengeVideo challenge={challenge} />
            <ChallengePromotionsSection challenge={challenge} />
            <ChallengeFAQ challenge={challenge} />
            <BrandLandingFooter darkMode={true} />
        </div>
    );
};

export default ChallengePage;